import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';

// ── The client's legal form, and what it may pay out ────────────────────────────────────────────
// Accounting Act s.8 lists the forms that must keep accounts. Until now the system only knew
// 'company' or 'individual', which cannot tell a registered partnership from a limited company —
// and that one distinction decides the DBD filing deadline, whether an audit may be waived, and
// which of the DBD's แบบ 1–5 the statements take.
//
// A form that has not been recorded shows as UNKNOWN in amber, never as "not required". The second
// would quietly excuse a company from the Act.

type Form = { code: string; th: string; en: string; keepsAccounts: boolean; dbdForm: string };
type Status = { form: string; th: string; en: string; keepsAccounts: boolean | null; dbdForm: string;
  registeredCapital: number; registeredOn: string; warnings: string[] };
type Account = { id: string; code: string; name_th: string; class: string };

const baht = (s: number) => money(Number(s || 0) / 100);
const satang = (v: string) => Math.round((parseFloat(String(v).replace(/,/g, '')) || 0) * 100);
const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};

export default function EntityInfo({ clientId, periodClosed, onChanged }:
  { clientId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [forms, setForms] = useState<Form[]>([]);
  const [st, setSt] = useState<Status | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [mode, setMode] = useState<'' | 'edit' | 'dividend'>('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [f, setF] = useState({ form: '', registeredCapital: '', registeredOn: '' });
  const [d, setD] = useState({ amount: '', resolutionDate: '', payableAccountId: '' });
  const [chk, setChk] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const s: any = await api.acctForm(clientId);
      setSt(s); setF({ form: s.form || '', registeredCapital: s.registeredCapital ? String(s.registeredCapital) : '', registeredOn: s.registeredOn || '' });
    } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.acctForms().then((r: any) => setForms(r.forms || [])).catch(() => setForms([]));
    api.jnlAccounts(clientId).then((r: any) => setAccounts(r.accounts || [])).catch(() => setAccounts([]));
  }, [clientId]);

  // The check runs as the amount is typed, because the answer is usually "no" and finding that out
  // after clicking is worse than knowing while deciding.
  useEffect(() => {
    if (mode !== 'dividend' || !satang(d.amount) || !d.resolutionDate) { setChk(null); return; }
    const h = setTimeout(() => {
      api.acctDividendCheck(clientId, satang(d.amount), d.resolutionDate).then(setChk).catch(() => setChk(null));
    }, 250);
    return () => clearTimeout(h);
  }, [mode, d.amount, d.resolutionDate, clientId]);

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      await api.acctSetForm(clientId, { form: f.form, registeredCapital: f.registeredCapital ? Number(f.registeredCapital) : undefined, registeredOn: f.registeredOn });
      setMode(''); await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const declare = async () => {
    setBusy('div'); setErr(''); setMsg('');
    try {
      const r: any = await api.acctPostDividend(clientId, { amount: satang(d.amount), resolutionDate: d.resolutionDate, payableAccountId: d.payableAccountId });
      setMsg(r.appropriation
        ? t('pa_entity_div_created_appr', { doc: r.docNo, amount: baht(r.appropriation) })
        : t('pa_entity_div_created', { doc: r.docNo }));
      setD({ amount: '', resolutionDate: '', payableAccountId: '' }); setChk(null); setMode(''); onChanged();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (!st) return null;
  const unknown = st.keepsAccounts === null;

  return (
    <div style={{ border: `1px solid ${unknown ? '#F1C232' : 'var(--border)'}`, borderRadius: 10, background: unknown ? '#FFFBEB' : 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className={unknown ? 'ti ti-alert-triangle' : 'ti ti-building-bank'} style={{ fontSize: 18, color: unknown ? '#B45309' : 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            {unknown ? t('pa_entity_form_not_recorded') : `${st.th} · ${st.en}`}
          </span>
          <span style={{ display: 'block', fontSize: 11, color: unknown ? '#92400E' : 'var(--text-tertiary)' }}>
            {unknown
              ? t('pa_entity_unknown_why')
              : `${st.keepsAccounts ? t('pa_entity_must_keep') : t('pa_entity_not_required')}${st.dbdForm ? ` · ${t('pa_entity_dbd_form', { n: st.dbdForm })}` : ''}${st.registeredCapital ? ` · ${t('pa_entity_reg_capital_val', { amount: baht(st.registeredCapital * 100) })}` : ''}${st.registeredOn ? ` · ${t('pa_entity_registered_on_val', { date: dmy(st.registeredOn) })}` : ''}`}
          </span>
        </span>
        <button onClick={() => setMode(m => m === 'edit' ? '' : 'edit')} style={btn}><i className="ti ti-edit" aria-hidden /> {t('edit')}</button>
        {st.keepsAccounts && !periodClosed && (
          <button onClick={() => setMode(m => m === 'dividend' ? '' : 'dividend')} style={btn}><i className="ti ti-cash" aria-hidden /> {t('pa_entity_dividend')}</button>
        )}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {mode === 'edit' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_f_form')}<br />
            <select value={f.form} onChange={e => setF({ ...f, form: e.target.value })} style={{ ...inp, width: 330 }}>
              <option value="">{t('pa_entity_f_form_select')}</option>
              {forms.map(x => <option key={x.code} value={x.code}>{x.th} — {x.en}{x.dbdForm ? ` ${t('pa_entity_form_dbd_suffix', { n: x.dbdForm })}` : ''}</option>)}
            </select></label>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_f_capital')}<br />
            <input value={f.registeredCapital} onChange={e => setF({ ...f, registeredCapital: e.target.value })} inputMode="decimal" style={{ ...inp, width: 150, textAlign: 'right' }} /></label>
          <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_f_registered_on')}<br />
            <input type="date" value={f.registeredOn} onChange={e => setF({ ...f, registeredOn: e.target.value })} style={{ ...inp, width: 150 }} /></label>
          <button onClick={save} disabled={!f.form || !!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>{t('save')}</button>
        </div>
      )}

      {mode === 'dividend' && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 9, lineHeight: 1.6 }}>
            {t('pa_entity_div_law')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_div_amount')}<br />
              <input value={d.amount} onChange={e => setD({ ...d, amount: e.target.value })} inputMode="decimal" style={{ ...inp, width: 150, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_div_resolution_date')}<br />
              <input type="date" value={d.resolutionDate} onChange={e => setD({ ...d, resolutionDate: e.target.value })} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_entity_div_payable_account')}<br />
              <select value={d.payableAccountId} onChange={e => setD({ ...d, payableAccountId: e.target.value })} style={{ ...inp, width: 250 }}>
                <option value="">—</option>
                {accounts.filter(a => a.class === 'liability').map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th}</option>)}
              </select></label>
          </div>
          {chk && (
            <div style={{ fontSize: 12, lineHeight: 1.8, marginBottom: 9 }}>
              <div style={{ color: 'var(--text-secondary)' }}>
                {t('pa_entity_div_retained', { amount: baht(chk.retainedEarnings) })} · {t('pa_entity_div_reserve', { amount: baht(chk.reserve) })}{chk.reserveCeiling ? ` / ${t('pa_entity_div_ceiling', { amount: baht(chk.reserveCeiling) })}` : ''}
                {chk.appropriation ? ` · ${t('pa_entity_div_appropriation', { amount: baht(chk.appropriation) })}` : ''}
              </div>
              {chk.blocked.map((b: string, i: number) => <div key={i} style={{ color: '#B42318', fontWeight: 700 }}><i className="ti ti-ban" aria-hidden /> {b}</div>)}
              {chk.notes.map((n: string, i: number) => <div key={i} style={{ color: '#92400E' }}><i className="ti ti-info-circle" aria-hidden /> {n}</div>)}
            </div>
          )}
          <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
            <button onClick={() => { setMode(''); setChk(null); }} style={btn}>{t('cancel')}</button>
            <button onClick={declare} disabled={!!busy || !chk || !!chk.blocked.length || !d.payableAccountId}
              style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none', opacity: (!chk || chk.blocked.length || !d.payableAccountId) ? 0.5 : 1 }}>
              <i className={busy === 'div' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_entity_div_create')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
